import React, { useEffect, useState } from 'react';
import SearchHotelsFilterInput from '@app/model/flexi_sets/input/SearchHotelsFilterInput';
import _isEmpty from 'lodash/isEmpty';
import FlexiSetFilterWrapper
	from '@app/site/secured/rateshopper/rates/dashboard/fragment/__flexi-sets/fragment/filter/FlexiSetFilterWrapper';
import Range from '@app/model/_common/Range';
import SearchHotelWithinRadiusResult from '@app/model/flexi_sets/SearchHotelWithinRadiusResult';
import DualRangeSlider from '@app/component/slider/DualRangeSlider';

export type FlexiSetGenericRangeFilterFilterConfig = {
	title: string;
	getHotelValue: (hotel: SearchHotelWithinRadiusResult) => number,
	increment: number,
	setToFilter: (filter: SearchHotelsFilterInput, range: Range) => void;
};

const FlexiSetGenericRangeFilterFragment: React.FunctionComponent<{
	areaHotels: SearchHotelWithinRadiusResult[],
	filter: SearchHotelsFilterInput,
	onFilter: (filter: SearchHotelsFilterInput) => void,
	config: FlexiSetGenericRangeFilterFilterConfig
}> = ({ areaHotels, filter, onFilter, config }) => {

	// Available range bounds (calculated from hotel data)
	const [availableRange, setAvailableRange] = useState<Range | undefined>(undefined);

	// Current user selection (starts as full range, user can narrow it down)
	const [currentSelection, setCurrentSelection] = useState<Range | undefined>(undefined);

	const [spread, setSpread] = useState<number[]>([]);

	useEffect(() => {
		if (!_isEmpty(areaHotels)) {
			const values = areaHotels.map(hotel => config.getHotelValue(hotel));

			const newAvailableRange = {
				from: Math.min(...values),
				to: Math.max(...values),
			};

			if (newAvailableRange.from >= 0 && newAvailableRange.to > 0) {
				if(_isEmpty(spread)) {
					const spreadSize = Math.floor((newAvailableRange.to - newAvailableRange.from) / config.increment);
					const spread: number[] = [];
					for (let i = 0; i <= spreadSize; i++) spread.push(0);

					values.forEach(v => {
						const index = Math.floor((v - newAvailableRange.from) / config.increment);
						spread[index]++;
					});

					setSpread(spread);
				}
				if (!availableRange)
					setAvailableRange(newAvailableRange);

				// Only initialize current selection if it's the first time or if available range changed significantly
				setCurrentSelection(prev => {
					if (!prev ||
						Math.abs(prev.from - newAvailableRange.from) > config.increment ||
						Math.abs(prev.to - newAvailableRange.to) > config.increment) {
						return newAvailableRange;
					}
					return prev;
				});
			}
		}
	}, [areaHotels, config]);

	// Don't render if no meaningful range
	if (!availableRange) {
		return null;
	}

	return <FlexiSetFilterWrapper title={config.title}>
		{/* Histogram showing value distribution */}
		{spread.length > 0 && (
			<div className="w-full h-12 flex items-end gap-px mb-4">
				{spread.map((count, index) => {
					const maxCount = Math.max(...spread);
					const height = maxCount > 0 ? (count / maxCount) * 100 : 0;
					return (
						<div
							key={index}
							className="bg-gray-300 flex-1 min-w-0"
							style={{
								height: `${height}%`,
								minHeight: count > 0 ? '2px' : '0px'
							}}
							title={`Range ${(availableRange.from + index * config.increment).toFixed(1)}-${(availableRange.from + (index + 1) * config.increment).toFixed(1)}: ${count} hotels`}
						/>
					);
				})}
			</div>
		)}

		<DualRangeSlider
			min={availableRange.from}
			max={availableRange.to}
			value={currentSelection}
			step={config.increment}
			onChange={(newRange) => {
				config.setToFilter(filter, newRange);
				onFilter(filter);
			}}
		/>
	</FlexiSetFilterWrapper>;
};

export default FlexiSetGenericRangeFilterFragment;
